import { semanticColors, typography } from '@moderneinc/design-system-tokens';
import { Stack, Typography } from '@mui/material';
import type { FunctionComponent, ReactNode } from 'react';

export type EmptyStateProps = {
  /** `title`/`description` so `ERROR_MESSAGES` entries spread in. */
  title: string;
  /** `ReactNode`, not `string`: several call sites pass JSX. */
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
};

/**
 * Prose, never alert chrome: a region with nothing in it is not a fault.
 * See doc/adr/0013-empty-states-are-not-alerts.md.
 */
export const EmptyState: FunctionComponent<EmptyStateProps> = ({
  title,
  description,
  icon,
  action
}) => (
  <Stack
    sx={{
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: 8,
      px: 3
    }}
  >
    {/* Muted here rather than per call site: the icon is orientation, not the
        message. `tertiary` is the muted token — `secondary` is a step too dark. */}
    {icon && (
      <Stack sx={{ mb: 2, color: semanticColors.color.text.tertiary }}>
        {icon}
      </Stack>
    )}
    {title && (
      <Typography
        component="h2"
        sx={{
          fontSize: typography.fontSize.h5,
          fontWeight: typography.fontWeight.semiBold,
          color: semanticColors.color.text.primary,
          mb: 1
        }}
      >
        {title}
      </Typography>
    )}
    {description && (
      <Typography
        component="div"
        sx={{
          fontSize: typography.fontSize.default,
          color: semanticColors.color.text.secondary,
          maxWidth: 480,
          textWrap: 'balance'
        }}
      >
        {description}
      </Typography>
    )}
    {action && <Stack sx={{ mt: 3 }}>{action}</Stack>}
  </Stack>
);
